import backbaseLogo from "../CV/img/backbase.webp";
import jedoLogo from "../CV/img/jedo.png";
import euriskoMobilityLogo from "../CV/img/euriskoMobility.jpg";
import tf961Logo from "../CV/img/tf961.jpeg";
import primwareLogo from "../CV/img/primware.jpeg";
import technologySARLLogo from "../CV/img/technologySARL.png";
import torchAcademyLogo from "../CV/img/torchAcademy.png";
import fusionSecondLogo from "../CV/img/fusionSecond.png";

/**
 * Single source of truth for professional experience.
 * Consumed by the CV page (src/CV/CV.js) and the homepage
 * experience section (src/Main/Experience/Experience.js).
 *
 * @typedef {Object} Role
 * @property {string} position
 * @property {string} from
 * @property {string} to
 * @property {string[]} highlights
 *
 * @typedef {Object} Company
 * @property {string} name
 * @property {string} [link]
 * @property {string} logo
 * @property {Role[]} roles
 */

/** @type {Company[]} */
export const experiences = [
  {
    name: "Backbase",
    link: "https://backbase.com",
    logo: backbaseLogo,
    roles: [
      {
        position: "Engineering Team Lead",
        from: "May 2026",
        to: "Present",
        highlights: [
          "Lead and manage a cross-platform team of 5 engineers, owning delivery, execution, and day-to-day team health.",
          "Partner closely with Product to shape and prioritize the roadmap, balancing sustainable team health against predictable delivery commitments.",
          "Established a culture of delivery predictability - coaching the team that reliable, accurate commitments matter more than optimistic estimates that slip, strengthening planning confidence across stakeholders.",
          "Navigate difficult prioritization conversations, breaking down and parallelizing large tickets across the team to accelerate throughput and unblock dependent work.",
          "Drive a company-wide initiative to deliver landscape and iPad support across all platforms (iOS, Android, and web), leveraging AI-native development to meet an aggressive deadline without compromising quality."
        ]
      },
      {
        position: "Principal iOS Engineer",
        from: "August 2023",
        to: "May 2026",
        highlights: [
          "Led technical direction across multiple core customer journeys, owning architecture, delivery trade-offs, and cross-team alignment.",
          "Acted as primary technical point of contact between Product and Engineering leadership, influencing scope, prioritization, and delivery feasibility.",
          "Drove the UIKit to SwiftUI migration strategy across the iOS guild, piloted on a production journey, validated feasibility, and defined the rollout plan.",
          "Designed and shipped a cross-platform PR testing system (iOS and Android), cutting CI/CD costs ~40% and improving feedback loops for the mobile teams.",
          "Mentored engineers and guided architectural decisions along with their personal development."
        ]
      },
      {
        position: "Senior iOS Engineer",
        from: "September 2021",
        to: "August 2023",
        highlights: [
          "Modernized the iOS release pipeline end-to-end - automated releases with Bitrise steps, Fastlane, and bash/node scripts; migrated CI from Jenkins to Bitrise, then led the migration on to GitHub Actions, delivering faster and more reliable releases.",
          "Cut iOS codebase maintenance overhead ~4x by merging four repositories into one and adopting XcodeGen to generate projects - reducing merge conflicts, improving consistency, and accelerating onboarding for new engineers."
        ]
      }
    ]
  },
  {
    name: "Jedo (Self-employed)",
    logo: jedoLogo,
    roles: [
      {
        position: "Co-Founder & CTO",
        from: "June 2023",
        to: "June 2024",
        highlights: [
          "Co-founded a startup as CTO - led a cross-functional team of 5 (3 engineers + 2 marketers), running hiring, technical direction, and delivery alongside hands-on development.",
          "Built and shipped the React Native mobile app, the Vue.js admin dashboard, and contributed to the Node.js backend - owning the full cross-platform stack end-to-end."
        ]
      }
    ]
  },
  {
    name: "Eurisko Mobility",
    link: "https://euriskomobility.com",
    logo: euriskoMobilityLogo,
    roles: [
      {
        position: "Senior iOS Consultant",
        from: "February 2021",
        to: "September 2021",
        highlights: [
          "Defined and rolled out a scalable architecture (Clean Swift / VIPER) - improving testability and long-term maintainability across the team.",
          "Designed and built a custom theming system enabling dynamic layout and branding adaptation across multiple screens and features.",
          "Developed reusable UI components and contributed to core application foundations, including authentication, security, notifications, and app lifecycle.",
          "Raised engineering standards across the iOS codebase - introducing quality gates, code review practices, and static analysis (SonarQube), and mentoring mid-level engineers on architecture and best practices."
        ]
      }
    ]
  },
  {
    name: "TF961",
    link: "https://tf961.com",
    logo: tf961Logo,
    roles: [
      {
        position: "Senior iOS Consultant",
        from: "April 2019",
        to: "February 2021",
        highlights: [
          "Led delivery of a production iOS application in under 4 months - balancing rapid iteration with strong engineering practices.",
          "Designed and implemented the application architecture using MVVM with RxSwift, focusing on separation of concerns, testability, and long-term maintainability.",
          "Maintained high code quality under tight deadlines through disciplined code reviews and static analysis tooling (SonarQube)."
        ]
      }
    ]
  },
  {
    name: "Primware (An ITG Company)",
    link: "https://www.itgholding.com/affiliate/16/primeware",
    logo: primwareLogo,
    roles: [
      {
        position: "Oracle Product Consultant",
        from: "March 2017",
        to: "April 2019",
        highlights: [
          "Developed solutions for clients in telecommunications, banking, money transfer, and government sectors.",
          "Traveled abroad to work onsite to deliver the highest quality.",
          "Led cross-border delivery teams and distributed work to meet tight, business-critical deadlines.",
          "Developed a custom solution to communicate with legacy systems (BSCS using CORBA) handling 250 transactions per second."
        ]
      }
    ]
  },
  {
    name: "Technology SARL",
    link: "http://technologysarl.com",
    logo: technologySARLLogo,
    roles: [
      {
        position: "iOS Developer",
        from: "February 2016",
        to: "February 2017",
        highlights: [
          "Refactored Objective-C codebase to Swift and contributed to backend services using Node.js and WebSockets."
        ]
      }
    ]
  },
  {
    name: "Torch Academy",
    link: "http://technologysarl.com",
    logo: torchAcademyLogo,
    roles: [
      {
        position: "iOS Tutor",
        from: "February 2016",
        to: "February 2017",
        highlights: [
          "Helped fresh graduate students develop ideas.",
          "Assisted entrepreneurs build minimum viable products (MVPs).",
          "Transferred best practices knowledge to students."
        ]
      }
    ]
  },
  {
    name: "Fusion Second",
    link: "https://fusionsecond.com",
    logo: fusionSecondLogo,
    roles: [
      {
        position: "iOS Developer Intern",
        from: "June 2015",
        to: "August 2015",
        highlights: ["Researched new technologies and ideas."]
      }
    ]
  }
];

/**
 * Flattened company/role pairs, in chronological (most recent first) order.
 * Convenient for views that render one entry per role.
 */
export const flattenedExperiences = experiences.flatMap((company) =>
  company.roles.map((role) => ({
    company: company.name,
    link: company.link,
    logo: company.logo,
    ...role
  }))
);

export const currentTitle = "Engineering Team Lead";

export const professionalProfile =
  "Engineering leader with 10+ years of experience building and shipping mobile products in fintech and enterprise environments. Track record of leading technical direction across customer-facing journeys, driving cross-functional alignment between product and engineering, and raising the bar on quality, delivery, and developer productivity. Combines hands-on technical credibility with a focus on people growth, mentorship, and scalable engineering practices.";
